import { useCallback, useState } from 'react';

import ApiCanceledError from 'lib/errors/ApiCanceledError';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiNonRetryableResponseError from 'lib/errors/ApiNonRetryableResponseError';
import ApiRequestError from 'lib/errors/ApiRequestError';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useIsMounted from 'lib/hooks/useIsMounted';

export type UnknownArgs = unknown[];

export type LoadFn<LoadArgs extends UnknownArgs = UnknownArgs, Ret = void> = (
  ...loadArgs: LoadArgs
) => Promise<Ret>;

export interface UseApiCallbackOptions {
  /**
   * Used to configure whether or not loading state should initialize as true
   * or false. If passing `setLoading`, `defaultLoading` will be ignored as it
   * is assumed that loading state is handled independently of this hook.
   */
  defaultLoading?: boolean;
}

interface UseApiCallbackReturn<LoadArgs extends UnknownArgs, Ret = void> {
  loading: boolean;
  error: Error | undefined;
  callback: LoadFn<LoadArgs, Ret>;
}

/**
 * @name `useApiCallback`
 *
 * A generic thunk wrapper that handles loading and error boilerplate. Takes a
 * thunk that returns a promise, and wraps it with loading and error handling.
 *
 * Can be thought of as `useCallback` that also provides handlers for loading
 * state and errors.
 *
 * Return values from the callbacks should be used sparingly, and only for
 * follow-up actions like rerouting the user. Read state from Redux instead of
 * from the return value of API actions.
 */
const useApiCallback = <LoadArgs extends UnknownArgs = UnknownArgs, Ret = void>(
  load: LoadFn<LoadArgs, Ret>,
  deps: unknown[],
  options: UseApiCallbackOptions = {},
): UseApiCallbackReturn<LoadArgs, Ret> => {
  const [loading, setLoading] = useState(!!options.defaultLoading);
  const [error, setError] = useState<Error | undefined>(undefined);
  const isMounted = useIsMounted();

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const callback = useCallback(async (...args: LoadArgs): Promise<Ret> => {
    let isCanceled = false;

    try {
      setLoading(true);
      const result = await load(...args);
      setError(undefined);
      return result;
    } catch (err) {
      if (err instanceof ApiCanceledError) {
        isCanceled = true;
      } else if (
        err instanceof ApiNetworkError ||
        err instanceof ApiResponseError ||
        err instanceof ApiRequestError ||
        err instanceof ApiNonRetryableResponseError
      ) {
        if (isMounted()) {
          setError(err);
        }
        // Only gracefully handle API errors
      } else {
        throw err;
      }
    } finally {
      if (isMounted() && !isCanceled) {
        setLoading(false);
      }
    }
  }, deps);

  return {
    loading,
    error,
    callback,
  };
};

export default useApiCallback;
